/* eslint-disable max-len */
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { DocumentRowComponent } from "./components/DocumentRow/DocumentRow";
import {
  getIsEditableStatusSearchInfo,
  getProspectStatus
} from "../../store/selectors/searchProspect";
import { updateProspect, updateRoProspect } from "../../store/actions/appConfig";
import { cancelDocUpload, docUpload } from "../../store/actions/uploadDocuments";
import { getProgress, getUploadErrors } from "../../store/selectors/uploadDocuments";
import { getProspectId, getDocuments, getRoDocuments } from "../../store/selectors/appConfig";
import { sendProspectToAPI } from "../../store/actions/sendProspectToAPI";
import {
  RO_DOCUMENTS,
  ADDITIONAL_DOCUMENTS,
  COMPANY_DOCUMENTS,
  STAKEHOLDER_DOCUMENTS,
  UPLOADED,
  COMPANY_ADDRESS_PROOF,
  SIGNATORY_DOCUMENTS,
  PROSPECT_DOCUMENT,
  SIGNBOARD_PROOF_DOC,
  OTHER_COMPANY_SIGNBOARD_PROOF_DOC,
  OUTSOURCED_STAFF_PROOF,
  COMPANY_KYC_DOCUMENTS,
  SIGNATORY_INVOLVEMENT_PROOF,
  SISTER_COMPANY_TL_PROOF,
  RO_SIGNATORY_DOCUMENTS,
  TELEPHONE_DEWA_PROOF
} from "../../constants";
import { DISABLED_STATUSES_FOR_UPLOAD_DOCUMENTS } from "./constants";
import {
  appendDocumentKey,
  getDocumentByUniqueId,
  getStakeholderDocumentByUniqueIdAndTitle,
  generateDocumentIdentifier
} from "../../utils/documents";
import { getDocumentFileName } from "../../utils/getDocumentFileName";

export const DocumentRow = ({
  document,
  type: docOwner,
  index,
  stakeholderIndex,
  docRemoveWarning = true,
  infoMessage = "",
  multiDoc = false,
  uploadMultiDocument = null,
  multiSelectedFile = null,
  required = false,
  displayValidationMsg = false,
  uniqueId = "",
  parentGroupTitle = ""
}) => {
  let documentIndex = 0;

  if (stakeholderIndex) {
    documentIndex =
      typeof stakeholderIndex === "number"
        ? stakeholderIndex
        : parseInt(stakeholderIndex.charAt(0));
  }
  const dispatch = useDispatch();

  const isApplyEditApplication = useSelector(getIsEditableStatusSearchInfo);
  const prospectStatusInfo = useSelector(getProspectStatus);
  const uploadErrors = useSelector(getUploadErrors);
  const progress = useSelector(getProgress);
  const [selectedFile, setSelectedFile] = useState(null);
  const prospectId = useSelector(getProspectId);
  const documents = useSelector(getDocuments);
  const roDocuments = useSelector(getRoDocuments);
  const isDisabledUploadForRO =
    isApplyEditApplication && DISABLED_STATUSES_FOR_UPLOAD_DOCUMENTS.includes(prospectStatusInfo);
  const { documentKey, documentType = "" } = document;
  const isUploaded = document.uploadStatus === UPLOADED;
  // ro-assist-brd2-1
  useEffect(() => {
    if (multiSelectedFile) {
      setSelectedFile(multiSelectedFile);
    }
  }, [multiSelectedFile]);
  const uploadDocument = useCallback(
    file => {
      if (!multiDoc) {
        const docProps = {
          uploadStatus: UPLOADED,
          fileSize: file.size,
          submittedDt: file.lastModifiedDate,
          fileFormat: file.type
        };
        const data = new FormData();
        data.append("documentType", documentType);
        data.append("documentIndex", documentIndex);
        data.append("documentKey", documentKey);
        data.append("uploadfile", file);
        data.append("fileFormat", file.type);
        data.append("fileName", getDocumentFileName(documentType, prospectId, file.name));

        dispatch(
          docUpload({
            data,
            docProps,
            docOwner,
            documentType,
            uniqueId,
            documentKey,
            index,
            userFileName: file.name,
            stakeholderIndex
          })
        );
        setSelectedFile(file);
      } else {
        uploadMultiDocument(file, document);
        setSelectedFile(file);
      }
    },
    [
      documentKey,
      documentType,
      docOwner,
      dispatch,
      index,
      stakeholderIndex,
      multiDoc,
      uploadMultiDocument,
      document,
      documentIndex,
      prospectId,
      uniqueId
    ]
  );
  /**
   * Handles deletion of company documents
   *
   * @param {Object} docs - The document object containing prospectDocument array
   * @param {number} docsIndex - Index of the document in the collection
   * @returns {void}
   */

  const companyMultiDocDelete = useCallback(
    (docs, docsIndex) => {
      // Note: Document duplication (RODOC21 -> RODOC7) only applies to uploads, not deletions
      // When a document is deleted from source, it should persist in the target

      if (docOwner === RO_DOCUMENTS) {
        if (docs.prospectDocument.length === 1) {
          dispatch(
            updateRoProspect({
              [`roProspect.documents[${COMPANY_DOCUMENTS}][${docsIndex}]['prospectDocument'][${index}]['uploadStatus']`]: "NotUploaded"
            })
          );
        } else {
          let newDocs = docs.prospectDocument.filter(
            (doc, docIndex) => parseInt(docIndex) !== parseInt(index)
          );
          newDocs = appendDocumentKey(newDocs);
          dispatch(
            updateRoProspect({
              [`roProspect.documents[${COMPANY_DOCUMENTS}][${docsIndex}]['prospectDocument']`]: newDocs
            })
          );
        }
      } else if (docOwner === ADDITIONAL_DOCUMENTS && docs.prospectDocument.length === 1) {
        // Handle additional documents case with single document
        dispatch(
          updateRoProspect({
            [`prospect.documents[${ADDITIONAL_DOCUMENTS}][${COMPANY_DOCUMENTS}][${docsIndex}]['prospectDocument'][${index}]['uploadStatus']`]: "NotUploaded"
          })
        );
      } else if (docOwner === ADDITIONAL_DOCUMENTS) {
        // Handle additional documents case with multiple documents
        let newDocs = docs.prospectDocument.filter(
          (doc, docIndex) => parseInt(docIndex) !== parseInt(index)
        );
        newDocs = appendDocumentKey(newDocs);
        dispatch(
          updateRoProspect({
            [`prospect.documents[${ADDITIONAL_DOCUMENTS}][${COMPANY_DOCUMENTS}][${docsIndex}]['prospectDocument']`]: newDocs
          })
        );
      } else if (docs.prospectDocument.length === 1) {
        // Handle single document case for docOwner other than RO_DOCUMENTS and ADDITIONAL_DOCUMENTS
        dispatch(
          updateProspect({
            [`prospect.documents[${docOwner}][${docsIndex}]['prospectDocument'][${index}]['uploadStatus']`]: "NotUploaded"
          })
        );
      } else {
        // Handle multiple documents case for docOwner other than RO_DOCUMENTS and ADDITIONAL_DOCUMENTS
        let newDocs = docs.prospectDocument.filter(
          (doc, docIndex) => parseInt(docIndex) !== parseInt(index)
        );
        newDocs = appendDocumentKey(newDocs);
        dispatch(
          updateProspect({
            [`prospect.documents[${docOwner}][${docsIndex}]['prospectDocument']`]: newDocs
          })
        );
      }
    },
    [dispatch, docOwner, index]
  );
  /**
   * Handles deletion of stakeholder documents
   *
   * @param {Object} docs - The document object containing prospectDocument array
   * @param {number} docsIndex - Index of the document in the collection
   * @returns {void}
   */
  const stakeholderMultiDocDelete = useCallback(
    (docs, docsIndex) => {
      if (docs.prospectDocument.length === 1) {
        if (docOwner === RO_DOCUMENTS) {
          dispatch(
            updateRoProspect({
              [`roProspect.documents[${STAKEHOLDER_DOCUMENTS}][${stakeholderIndex}]['documents'][${docsIndex}].prospectDocument[${index}].uploadStatus`]: "NotUploaded"
            })
          );
        } else if (docOwner === ADDITIONAL_DOCUMENTS) {
          dispatch(
            updateRoProspect({
              [`prospect.documents[${ADDITIONAL_DOCUMENTS}][${STAKEHOLDER_DOCUMENTS}][${stakeholderIndex}]['documents'][${docsIndex}].prospectDocument[${index}].uploadStatus`]: "NotUploaded"
            })
          );
        } else {
          dispatch(
            updateProspect({
              [`prospect.documents[${STAKEHOLDER_DOCUMENTS}][${stakeholderIndex}]['documents'][${docsIndex}].prospectDocument[${index}].uploadStatus`]: "NotUploaded"
            })
          );
        }
      } else {
        let newDocs = docs.prospectDocument.filter(
          (doc, docIndex) => parseInt(docIndex) !== parseInt(index)
        );
        newDocs = appendDocumentKey(newDocs);

        if (docOwner === RO_DOCUMENTS) {
          dispatch(
            updateRoProspect({
              [`roProspect.documents[${STAKEHOLDER_DOCUMENTS}][${stakeholderIndex}]['documents'][${docsIndex}].prospectDocument`]: newDocs
            })
          );
        } else if (docOwner === ADDITIONAL_DOCUMENTS) {
          dispatch(
            updateRoProspect({
              [`prospect.documents[${ADDITIONAL_DOCUMENTS}][${STAKEHOLDER_DOCUMENTS}][${stakeholderIndex}]['documents'][${docsIndex}].prospectDocument`]: newDocs
            })
          );
        } else {
          dispatch(
            updateProspect({
              [`prospect.documents[${STAKEHOLDER_DOCUMENTS}][${stakeholderIndex}]['documents'][${docsIndex}].prospectDocument`]: newDocs
            })
          );
        }
      }
    },
    [dispatch, docOwner, index, stakeholderIndex]
  );

  const fileUploadCancel = useCallback(() => {
    if (docOwner === STAKEHOLDER_DOCUMENTS) {
      const {
        docIndex: docsIndex,
        documents: existingDocs
      } = getStakeholderDocumentByUniqueIdAndTitle(
        documents,
        uniqueId,
        stakeholderIndex,
        parentGroupTitle || document.documentTitle
      );
      stakeholderMultiDocDelete(existingDocs, docsIndex);
    } else if (docOwner === RO_DOCUMENTS) {
      const {
        docIndex: docsIndex,
        documents: existingDocs
      } = getStakeholderDocumentByUniqueIdAndTitle(
        roDocuments,
        uniqueId,
        stakeholderIndex,
        parentGroupTitle || document.documentTitle
      );
      stakeholderIndex
        ? stakeholderMultiDocDelete(existingDocs, docsIndex)
        : companyMultiDocDelete(existingDocs, docsIndex);
    } else if (docOwner === ADDITIONAL_DOCUMENTS) {
      const {
        docIndex: docsIndex,
        documents: existingDocs
      } = getStakeholderDocumentByUniqueIdAndTitle(
        documents[ADDITIONAL_DOCUMENTS],
        uniqueId,
        stakeholderIndex,
        parentGroupTitle || document.documentTitle
      );

      stakeholderIndex
        ? stakeholderMultiDocDelete(existingDocs, docsIndex)
        : companyMultiDocDelete(existingDocs, docsIndex);
    } else {
      const { docIndex: docsIndex, documents: existingDocs } = getDocumentByUniqueId(
        documents,
        uniqueId
      );
      companyMultiDocDelete(existingDocs, docsIndex);
    }
    const noProspectDocuments = [
      SIGNATORY_DOCUMENTS,
      COMPANY_ADDRESS_PROOF,
      PROSPECT_DOCUMENT,
      SIGNBOARD_PROOF_DOC,
      OTHER_COMPANY_SIGNBOARD_PROOF_DOC,
      OUTSOURCED_STAFF_PROOF,
      COMPANY_KYC_DOCUMENTS,
      SIGNATORY_INVOLVEMENT_PROOF,
      SISTER_COMPANY_TL_PROOF,
      RO_SIGNATORY_DOCUMENTS,
      TELEPHONE_DEWA_PROOF
    ];

    dispatch(cancelDocUpload(documentKey));
    setSelectedFile(null);
    if (!noProspectDocuments.includes(docOwner)) dispatch(sendProspectToAPI());
  }, [
    dispatch,
    docOwner,
    documentKey,
    stakeholderIndex,
    roDocuments,
    documents,
    uniqueId,
    companyMultiDocDelete,
    stakeholderMultiDocDelete,
    parentGroupTitle,
    document.documentTitle
  ]);

  const reUploadHandler = useCallback(() => {
    setSelectedFile(null);
  }, []);
  const uniqueIdentifier = generateDocumentIdentifier(stakeholderIndex, uniqueId, documentKey);

  return (
    <DocumentRowComponent
      isUploadError={uploadErrors[uniqueIdentifier]}
      identifier={uniqueIdentifier}
      isUploaded={isUploaded}
      isUploading={!!selectedFile && !isUploaded}
      percentComplete={isUploaded ? 100 : progress[documentKey] || 0}
      selectedFile={selectedFile}
      uploadDocument={uploadDocument}
      isDisabledUploadForRO={isDisabledUploadForRO}
      reUploadHandler={reUploadHandler}
      cancelHandler={fileUploadCancel}
      document={document}
      docRemoveWarning={docRemoveWarning}
      infoMessage={infoMessage}
      multiDoc={multiDoc}
      required={required}
      displayValidationMsg={displayValidationMsg}
      uniqueId={uniqueId}
      parentGroupTitle={parentGroupTitle}
    />
  );
};

/**
 * PropTypes for DocumentRow component
 */
DocumentRow.propTypes = {
  /**
   * Document object containing metadata and file details
   */
  document: PropTypes.shape({
    /**
     * Unique key identifier for the document
     */
    documentKey: PropTypes.string.isRequired,

    /**
     * Type of document
     */
    documentType: PropTypes.string,

    /**
     * Current upload status of the document
     */
    uploadStatus: PropTypes.string,

    /**
     * Optional title for the document
     */
    documentTitle: PropTypes.string,

    /**
     * Array of prospect documents
     */
    prospectDocument: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Document identifier key
         */
        documentKey: PropTypes.string,

        /**
         * Upload status (e.g., "Uploaded", "NotUploaded")
         */
        uploadStatus: PropTypes.string,

        /**
         * Name of the file
         */
        fileName: PropTypes.string,

        /**
         * Size of the file in bytes
         */
        fileSize: PropTypes.number,

        /**
         * Format/MIME type of the file
         */
        fileFormat: PropTypes.string
      })
    )
  }).isRequired,

  /**
   * Type of document owner (e.g., COMPANY_DOCUMENTS, STAKEHOLDER_DOCUMENTS)
   */
  type: PropTypes.string.isRequired,

  /**
   * Index of the document in the list
   */
  index: PropTypes.number,

  /**
   * Index of the stakeholder if document belongs to a stakeholder
   */
  stakeholderIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Whether to show document removal warning
   */
  docRemoveWarning: PropTypes.bool,

  /**
   * Additional information message for the document
   */
  infoMessage: PropTypes.string,

  /**
   * Whether this is a multi-document upload component
   */
  multiDoc: PropTypes.bool,

  /**
   * Function to upload documents in multi-document mode
   */
  uploadMultiDocument: PropTypes.func,

  /**
   * Selected file in multi-document mode
   */
  multiSelectedFile: PropTypes.object,

  /**
   * Whether the document is required
   */
  required: PropTypes.bool,

  /**
   * Whether to display validation messages
   */
  displayValidationMsg: PropTypes.bool,

  /**
   * Unique identifier for the document category
   */
  uniqueId: PropTypes.string,
  parentGroupTitle: PropTypes.string
};
